import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Image as ImageIcon, Upload, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminHero() {
  const [heroImages, setHeroImages] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    description: '',
    order: 0
  });

  useEffect(() => {
    fetchHeroImages();
  }, []);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(`${API}/hero-images`);
      setHeroImages(response.data);
    } catch (error) {
      toast.error('Failed to fetch hero images');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData({ ...formData, url: response.data.url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    const token = sessionStorage.getItem('token');

    try {
      await axios.post(
        `${API}/hero-images`,
        {
          ...formData,
          order: heroImages.length
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Hero image added successfully!');
      setIsAddDialogOpen(false);
      setFormData({ url: '', title: '', description: '', order: 0 });
      fetchHeroImages();
    } catch (error) {
      toast.error('Failed to add hero image');
    }
  };

  const handleUpdateImage = async (e) => {
    e.preventDefault();
    const token = sessionStorage.getItem('token');

    try {
      await axios.put(
        `${API}/hero-images/${editingImage.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Hero image updated successfully!');
      setIsEditDialogOpen(false);
      setEditingImage(null);
      fetchHeroImages();
    } catch (error) {
      toast.error('Failed to update hero image');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this hero image?')) return;

    const token = sessionStorage.getItem('token');
    try {
      await axios.delete(`${API}/hero-images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Hero image deleted successfully!');
      fetchHeroImages();
    } catch (error) {
      toast.error('Failed to delete hero image');
    }
  };

  const openEditDialog = (image) => {
    setEditingImage(image);
    setFormData({
      url: image.url,
      title: image.title || '',
      description: image.description || '',
      order: image.order
    });
    setIsEditDialogOpen(true);
  };

  const moveImage = async (index, direction) => {
    const newImages = [...heroImages];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newImages.length) return;
    
    // Swap order values
    const currentOrder = newImages[index].order;
    const targetOrder = newImages[targetIndex].order;
    
    const token = sessionStorage.getItem('token');
    try {
      await Promise.all([
        axios.put(`${API}/hero-images/${newImages[index].id}`, { ...newImages[index], order: targetOrder }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.put(`${API}/hero-images/${newImages[targetIndex].id}`, { ...newImages[targetIndex], order: currentOrder }, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      fetchHeroImages();
    } catch (error) {
      toast.error('Failed to update image order');
    }
  };

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="admin-hero-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="hero-title">Manage Hero Images</h1>
              <p className="text-muted-foreground">Add, edit, or remove images displayed in the home page carousel</p>
            </div>
            
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-primary hover:bg-primary/90 w-fit" data-testid="add-hero-button">
                  <Plus className="mr-2 h-5 w-5" />
                  Add Image
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Hero Image</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddImage} className="space-y-4" data-testid="add-hero-form">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title (Optional)</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-white/5 border-white/10"
                      data-testid="hero-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-white/5 border-white/10"
                      data-testid="hero-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="url">Image URL</Label>
                      <label className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 px-3 py-2 variant-ghost">
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                        <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                      </label>
                    </div>
                    {uploading && <p className="text-xs text-primary text-right">Uploading...</p>}
                    <Input
                      id="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      className="bg-white/5 border-white/10"
                      required
                      placeholder="https://example.com/image.jpg"
                      data-testid="hero-url-input"
                    />
                  </div>
                  <Button type="submit" className="w-full rounded-full bg-primary" data-testid="submit-hero-button">
                    Add Image
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {heroImages.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="glass-morphic rounded-2xl overflow-hidden border-white/5 group"
                data-testid={`hero-card-${index}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={getImageUrl(image.url)}
                    alt={image.title || "Hero"}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                    <Button size="icon" variant="secondary" className="rounded-full" onClick={() => openEditDialog(image)} data-testid={`edit-hero-${index}`}>
                      <Edit className="h-5 w-5" />
                    </Button>
                    <Button size="icon" variant="destructive" className="rounded-full" onClick={() => handleDeleteImage(image.id)} data-testid={`delete-hero-${index}`}>
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-semibold truncate">{image.title || 'No Title'}</h3>
                    <div className="flex gap-1">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8" 
                        disabled={index === 0}
                        onClick={() => moveImage(index, 'up')}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8" 
                        disabled={index === heroImages.length - 1}
                        onClick={() => moveImage(index, 'down')}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm line-clamp-2">{image.description || 'No description'}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {heroImages.length === 0 && (
            <div className="text-center py-20 glass-morphic rounded-2xl" data-testid="no-hero-images">
              <ImageIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-xl text-muted-foreground">No hero images found</p>
            </div>
          )}
        </motion.div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Hero Image</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateImage} className="space-y-4" data-testid="edit-hero-form">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title (Optional)</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-white/5 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (Optional)</Label>
              <Input
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-url">Image URL</Label>
                <label className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 px-3 py-2 variant-ghost">
                  <Upload className="h-4 w-4 mr-1" />
                  Upload
                  <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                </label>
              </div>
              {uploading && <p className="text-xs text-primary text-right">Uploading...</p>}
              <Input
                id="edit-url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="bg-white/5 border-white/10"
                required
              />
            </div>
            <Button type="submit" className="w-full rounded-full bg-primary">
              Update Image
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
